import React, { useState, useEffect } from 'react';

const IFAddBatchForm = () => {
  const [formData, setFormData] = useState({
    program: '',
    institute: '',
    batchName: '',
    startDate: '',
    endDate: '',
    batchType: 'Regular',
    days: [],
    time: '',
    status: 'Active',
    capacity: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  // Mock data for dropdowns
  const programs = [
    { id: 1, name: 'Data Science Basics', duration: '3 months' },
    { id: 2, name: 'Full Stack Development', duration: '6 months' },
    { id: 3, name: 'Advanced ML Concepts', duration: '4 months' },
    { id: 4, name: 'Mobile App Development', duration: '5 months' },
    { id: 5, name: 'Cloud & DevOps', duration: '3 months' }
  ];

  const institutes = [
    { id: 1, name: 'Tech Institute Mumbai', location: 'Mumbai' },
    { id: 2, name: 'Digital Learning Center', location: 'Delhi' },
    { id: 3, name: 'Innovation Academy', location: 'Bangalore' },
    { id: 4, name: 'Future Skills Institute', location: 'Pune' }
  ];

  const batchTypes = [
    { value: 'Regular', label: 'Regular (Weekdays)', description: 'Monday to Friday' },
    { value: 'Weekend', label: 'Weekend', description: 'Saturday and Sunday' },
    { value: 'Intensive', label: 'Intensive', description: 'Daily including weekends' }
  ];

  const statusOptions = ['Active', 'Inactive'];
  
  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  // Auto-populate days based on batch type
  useEffect(() => {
    let selectedDays = [];
    switch (formData.batchType) {
      case 'Regular':
        selectedDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
        break;
      case 'Weekend':
        selectedDays = ['saturday', 'sunday'];
        break;
      case 'Intensive':
        selectedDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        break;
      default:
        selectedDays = [];
    }
    setFormData(prev => ({ ...prev, days: selectedDays }));
  }, [formData.batchType]);

  // Validation functions
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.program) newErrors.program = 'Program is required';
    if (!formData.institute) newErrors.institute = 'Institute is required';
    if (!formData.batchName.trim()) newErrors.batchName = 'Batch name is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!formData.capacity || formData.capacity < 1) newErrors.capacity = 'Valid capacity is required';
    
    // Date validation
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (startDate < today) {
        newErrors.startDate = 'Start date cannot be in the past';
      }
      
      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }
    
    // Days validation
    if (formData.days.length === 0) {
      newErrors.days = 'At least one day must be selected';
    }

    // Batch name validation (should be unique-ish)
    if (formData.batchName.length < 3) {
      newErrors.batchName = 'Batch name must be at least 3 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDayChange = (day) => {
    setFormData(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
    
    if (errors.days) {
      setErrors(prev => ({ ...prev, days: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitMessage({
        type: 'danger',
        text: 'Please fix the errors below before submitting.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate batch code
      const batchCode = `${formData.program.substring(0, 3).toUpperCase()}-${Date.now().toString().slice(-4)}`;
      
      const submitData = {
        ...formData,
        batchCode,
        createdAt: new Date().toISOString(),
        daysText: formData.days.map(day => 
          daysOfWeek.find(d => d.value === day)?.label
        ).join(', ')
      };
      
      console.log('Batch created successfully:', submitData);
      
      setSubmitMessage({
        type: 'success',
        text: `Batch "${formData.batchName}" created successfully with code: ${batchCode}`
      });
      
      // Reset form after successful submission
      setTimeout(() => {
        resetForm();
      }, 3000);
      
    } catch (error) {
      console.error('Error creating batch:', error);
      setSubmitMessage({
        type: 'danger',
        text: 'Failed to create batch. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      program: '',
      institute: '',
      batchName: '',
      startDate: '',
      endDate: '',
      batchType: 'Regular',
      days: [],
      time: '',
      status: 'Active',
      capacity: '',
      description: ''
    });
    setErrors({});
    setSubmitMessage({ type: '', text: '' });
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between bg-transparent mb-4">
            <h4 className="mb-sm-0 fw-bold text-dark">Create New Batch</h4>
            <div className="page-title-right">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a href="/teacher-dashboard" className="text-decoration-none">Dashboard</a>
                  </li>
                  <li className="breadcrumb-item">
                    <a href="/teacher-batches" className="text-decoration-none">Batches</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">Add New Batch</li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Messages */}
      {submitMessage.text && (
        <div className="row mb-3">
          <div className="col-12">
            <div className={`alert alert-${submitMessage.type} alert-dismissible fade show`} role="alert">
              <strong>
                {submitMessage.type === 'success' ? '✓ Success!' : '⚠ Error!'}
              </strong>
              {' ' + submitMessage.text}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setSubmitMessage({ type: '', text: '' })}
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-primary text-white">
              <h5 className="card-title text-white mb-0">
                Batch Information
              </h5>
            </div>
            <div className="card-body p-4">
              <div>
                {/* Program and Institute */}
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Program <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.program ? 'is-invalid' : ''}`}
                      name="program"
                      value={formData.program}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Choose a program...</option>
                      {programs.map(program => (
                        <option key={program.id} value={program.name}>
                          {program.name} ({program.duration})
                        </option>
                      ))}
                    </select>
                    {errors.program && <div className="invalid-feedback d-block">{errors.program}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Institute <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.institute ? 'is-invalid' : ''}`}
                      name="institute"
                      value={formData.institute}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Choose an institute...</option>
                      {institutes.map(institute => (
                        <option key={institute.id} value={institute.name}>
                          {institute.name} - {institute.location}
                        </option>
                      ))}
                    </select>
                    {errors.institute && <div className="invalid-feedback d-block">{errors.institute}</div>}
                  </div>
                  
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Batch Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.batchName ? 'is-invalid' : ''}`}
                      name="batchName"
                      value={formData.batchName}
                      onChange={handleInputChange}
                      placeholder="Enter batch name (e.g., Morning Batch A1)"
                      required
                    />
                    {errors.batchName && <div className="invalid-feedback d-block">{errors.batchName}</div>}
                  </div>
                </div>

                {/* Batch Name and Dates */}
                <div className="row">

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Start Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                    {errors.startDate && <div className="invalid-feedback d-block">{errors.startDate}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      End Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      min={formData.startDate || new Date().toISOString().split('T')[0]}
                      required
                    />
                    {errors.endDate && <div className="invalid-feedback d-block">{errors.endDate}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className={`form-control ${errors.time ? 'is-invalid' : ''}`}
                      name="time"
                      onClick={(e)=>{
                        if(e.target.type=='time'){
                          // e.target.showPicker?.()
                        }
                      }}
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                    />
                    {errors.time && <div className="invalid-feedback d-block">{errors.time}</div>}
                  </div>
                </div>

                {/* Batch Type and Time */}
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">Batch Type</label>
                    <select
                      className="form-select"
                      name="batchType"
                      value={formData.batchType}
                      onChange={handleInputChange}
                    >
                      {batchTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label} - {type.description}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">Status</label>
                    <select
                      className="form-select"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Capacity <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${errors.capacity ? 'is-invalid' : ''}`}
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      placeholder="Maximum number of students"
                      min="1"
                      max="500"
                      required
                    />
                    {errors.capacity && <div className="invalid-feedback d-block">{errors.capacity}</div>}
                  </div>
                </div>

                {/* Days Selection */}
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Days <span className="text-danger">*</span>
                    </label>
                    <div className="border rounded p-3 bg-light">
                      <div className="row">
                        {daysOfWeek.map(day => (
                          <div key={day.value} className="col-md-3 col-sm-6 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={day.value}
                                checked={formData.days.includes(day.value)}
                                onChange={() => handleDayChange(day.value)}
                              />
                              <label className="form-check-label" htmlFor={day.value}>
                                {day.label}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {errors.days && <div className="text-danger small mt-1">{errors.days}</div>}
                  </div>
                </div>

                {/* Status and Capacity */}
                <div className="row">
                  
                </div>

                {/* Description */}
                <div className="row">
                  <div className="col-12 mb-4">
                    <label className="form-label fw-semibold text-dark">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Brief description about the batch, prerequisites, or special notes..."
                    />
                    <div className="form-text">
                      {formData.description.length}/500 characters
                    </div>
                  </div>
                </div>

                {/* Summary Card */}
                {(formData.program || formData.batchName) && (
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card bg-light border-primary">
                        <div className="card-header bg-primary">
                          <h6 className="mb-0 text-light">📋 Batch Summary</h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <p className="mb-2">
                                <strong>Program:</strong> {formData.program || 'Not selected'}
                              </p>
                              <p className="mb-2">
                                <strong>Batch Name:</strong> {formData.batchName || 'Not specified'}
                              </p>
                              <p className="mb-2">
                                <strong>Institute:</strong> {formData.institute || 'Not selected'}
                              </p>
                            </div>
                            <div className="col-md-6">
                              <p className="mb-2">
                                <strong>Duration:</strong> {formData.startDate && formData.endDate 
                                  ? `${formData.startDate} to ${formData.endDate}` 
                                  : 'Not specified'}
                              </p>
                              <p className="mb-2">
                                <strong>Time:</strong> {formData.time || 'Not specified'}
                              </p>
                              <p className="mb-2">
                                <strong>Days:</strong> {formData.days.length > 0 
                                  ? formData.days.map(day => 
                                      daysOfWeek.find(d => d.value === day)?.label
                                    ).join(', ')
                                  : 'Not selected'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="row">
                  <div className="col-12">
                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-primary px-4 py-2"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Creating Batch...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check me-1"></i> Create Batch
                          </>
                        )}
                      </button>
                      
                      <button
                        type="button"
                        className="btn btn-outline-secondary px-4 py-2"
                        onClick={resetForm}
                        disabled={isSubmitting}
                      >
                        <i className="fas fa-sync-alt me-1"></i> Reset Form
                      </button>
                      
                      <button
                        type="button"
                        className="btn btn-outline-danger px-5 py-2"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to cancel? All data will be lost.')) {
                            resetForm();
                            // In a real app, navigate back
                            console.log('Navigate back to batches list');
                          }
                        }}
                        disabled={isSubmitting}
                      >
                        <i className="fas fa-times me-1"></i> Cancel
                      </button>
                      
                      <a
                        href="/teacher-batches"
                        className="btn btn-outline-primary px-3 py-2"
                      >
                        <i className="fas fa-arrow-left me-1"></i> Go to Batches
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IFAddBatchForm;
    
